// DOCS → TASKS — the work, and what proves it.
//
// Every task the project has planned or filed, from both sides of the chain:
// the ones extracted from requirement documents and the ones created FROM a
// feature. Each row links back to the feature that asked for it and forward to
// the Board card that delivers it, and carries the EVIDENCE that lets a person
// check the thing is really done.
//
// The evidence column is the point of the tab: "done" is a claim, and this is
// where the claim is backed.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, RefreshCw, ChevronDown, ChevronRight, ExternalLink, ListChecks, Check, Plus, Paperclip,
} from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";

// Presigned PUT — the same handshake materials, board attachments and chat
// uploads use (fetchData injects the auth + project headers). Returns the S3
// key, which becomes the evidence `ref`; the bytes are read back through the
// authenticated /evidence/:id/image route, never by presigned GET (whose URL
// can name a host only the backend can reach).
async function uploadScreenshot(file) {
  const signed = await fetchData("chat/upload/sign", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename: file.name, content_type: file.type || "application/octet-stream" }),
  });
  const put = await fetch(signed.url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!put.ok) throw new Error(`upload failed (HTTP ${put.status})`);
  return signed.key;
}

const boardTone = (st) => st === "done" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
  : st === "running" || st === "claimed" || st === "queued" ? "bg-nav-active/10 text-nav-active"
  : st === "waiting" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
  : st === "failed" || st === "cancelled" ? "bg-red-500/10 text-red-700 dark:text-red-400"
  : "bg-card-border/40 text-page-muted";
const boardLabel = (st) => st === "done" ? "✓ done"
  : st === "running" || st === "claimed" ? "in progress"
  : st === "queued" ? "queued for a worker"
  : st === "waiting" ? "blocked — needs you"
  : st === "todo" ? "in the backlog"
  : st || "on the Board";

const EV_KINDS = [
  ["steps", "Steps to verify"],
  ["test_notes", "How it was tested"],
  ["commit", "Commit"],
  ["screenshot", "Screenshot"],
  ["link", "Link"],
];

export default function TasksTab({ projectSlug, onOpenFeature, focusId, onFocusHandled }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(() => new Set());
  const [evidence, setEvidence] = useState({});
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await fetchData("docs/tasks", { method: "GET" });
      setData(r);
    } catch (e) {
      setData({ tasks: [] });
      setErr(String(e?.message || "Could not read the tasks."));
    }
  }, []);
  useEffect(() => { setData(null); load(); }, [projectSlug, load]);

  const loadEvidence = useCallback(async (taskId) => {
    try {
      const r = await fetchData(`docs/tasks/${encodeURIComponent(taskId)}/evidence`, { method: "GET" });
      setEvidence((m) => ({ ...m, [taskId]: r?.evidence || [] }));
    } catch { setEvidence((m) => ({ ...m, [taskId]: [] })); }
  }, []);

  const toggle = (id) => setOpen((o) => {
    const n = new Set(o);
    if (n.has(id)) n.delete(id); else { n.add(id); if (!evidence[id]) loadEvidence(id); }
    return n;
  });

  // Deep link from the Timeline: open that task with its evidence loaded — a
  // task event is almost always about the evidence on it, so landing on a
  // collapsed row would hide the very thing the link was about.
  useEffect(() => {
    if (!focusId || !data) return;
    if ((data.tasks || []).some((t) => t.id === focusId)) {
      setOpen((o) => new Set(o).add(focusId));
      if (!evidence[focusId]) loadEvidence(focusId);
      requestAnimationFrame(() => {
        document.querySelector(`[data-testid="task-${CSS.escape(focusId)}"]`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    }
    onFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, data]);

  const tasks = data?.tasks || [];
  const shown = useMemo(() => tasks.filter((t) =>
    filter === "all" ? true
    : filter === "filed" ? !!t.request_id
    : filter === "done" ? t.board_status === "done"
    : filter === "evidence" ? t.evidence > 0
    : filter === "unproven" ? (t.board_status === "done" && !t.evidence)
    : true), [tasks, filter]);

  const counts = useMemo(() => ({
    total: tasks.length,
    filed: tasks.filter((t) => t.request_id).length,
    done: tasks.filter((t) => t.board_status === "done").length,
    evidence: tasks.filter((t) => t.evidence > 0).length,
    unproven: tasks.filter((t) => t.board_status === "done" && !t.evidence).length,
  }), [tasks]);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="tasks-tab">
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          <ListChecks size={20} /> Tasks
        </h1>
        <div className="text-sm text-page-muted mt-1">
          the work behind <b>{projectSlug}</b>'s requirements — what was planned, what is
          on the Board, and what has evidence you can check.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Chip>{counts.total} task(s)</Chip>
        <Chip>{counts.filed} filed</Chip>
        {counts.done > 0 ? <Chip tone="text-emerald-700 dark:text-emerald-400">{counts.done} done</Chip> : null}
        {counts.evidence > 0 ? <Chip tone="text-emerald-700 dark:text-emerald-400">{counts.evidence} with evidence</Chip> : null}
        {counts.unproven > 0 ? (
          <Chip tone="text-amber-700 dark:text-amber-400">{counts.unproven} done without evidence</Chip>
        ) : null}
        <select data-testid="tasks-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="ml-2 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text cursor-pointer">
          <option value="all">All</option>
          <option value="filed">On the Board</option>
          <option value="done">Done</option>
          <option value="evidence">With evidence</option>
          <option value="unproven">Done without evidence</option>
        </select>
        <button type="button" data-testid="tasks-refresh" onClick={load}
          className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1" title="Reload">
          <RefreshCw size={13} />
        </button>
      </div>

      {err ? <div className="text-sm text-red-600" data-testid="tasks-error">{err}</div> : null}

      {/* THE BUILDS — one per sources version that was (or is being) built, and
          the stage line that says WHY the requirements analysis is open or
          gated right now. The build is the first task of a sources version;
          the requirement tasks below are the gaps the analysis found after it. */}
      {data?.stage?.materials_version ? <StageLine stage={data.stage} /> : null}
      {(data?.builds || []).length ? (
        <div className="space-y-1" data-testid="tasks-builds">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted pt-1">
            Builds from requirement sources <span className="font-normal">· {data.builds.length}</span>
          </div>
          {data.builds.map((b) => <BuildRow key={b.materials_version} b={b} projectSlug={projectSlug} />)}
          <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted pt-3">
            Requirement tasks <span className="font-normal">· {tasks.length}</span>
          </div>
        </div>
      ) : null}

      {data == null && (
        <div className="flex items-center gap-2 text-sm text-page-muted"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      )}
      {data != null && tasks.length === 0 && !err && (
        <div className="rounded-lg border border-page-divider p-4 text-sm text-page-text space-y-2" data-testid="tasks-empty">
          <div className="font-medium">No requirement tasks yet.</div>
          <div className="text-page-muted">
            {(data?.builds || []).length
              ? "The project is built from its requirement sources first (the builds above). Requirement tasks appear once the analysis has run and found gaps — press Analyse this project on the Requirements tab after the build is done."
              : "Tasks are created from a requirement on the Requirements tab, or extracted from a requirement document."}
          </div>
        </div>
      )}

      <div className="space-y-1">
        {shown.map((t) => {
          const isOpen = open.has(t.id);
          return (
            <div key={t.id} data-testid={`task-${t.id}`} className="rounded-md border border-page-divider">
              <div className="flex items-center gap-2 px-2 py-1.5">
                <button type="button" data-testid={`task-toggle-${t.id}`} onClick={() => toggle(t.id)}
                  className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0 shrink-0">
                  {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                </button>
                <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-card-border/40 text-page-muted">{t.kind}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-page-text" title={t.title}>{t.title}</span>
                {t.feature ? (
                  <button type="button" onClick={() => onOpenFeature?.(t.feature)} data-testid={`task-feature-${t.id}`}
                    title="The feature this task belongs to"
                    className="shrink-0 text-[11px] text-nav-active bg-transparent border-0 p-0 cursor-pointer hover:underline">
                    {t.feature}
                  </button>
                ) : null}
                {t.request_id ? (
                  <a href={`/p/${encodeURIComponent(projectSlug)}/b/main/board`}
                    className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded no-underline hover:underline ${boardTone(t.board_status)}`}>
                    #{t.request_id} · {boardLabel(t.board_status)}
                  </a>
                ) : <span className="shrink-0 text-[11px] text-page-muted">not filed</span>}
                <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded ${t.evidence ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-card-border/40 text-page-muted"}`}
                  data-testid={`task-evidence-count-${t.id}`}>
                  {t.evidence ? `${t.evidence} evidence` : "no evidence"}
                </span>
              </div>
              {isOpen ? (
                <div className="px-2 pb-2 pl-8 space-y-2">
                  {t.brief ? <div className="text-xs text-page-muted whitespace-pre-wrap">{t.brief}</div> : null}
                  <Evidence taskId={t.id} rows={evidence[t.id]}
                    onChanged={async () => { await loadEvidence(t.id); await load(); }} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// sources vN → preview → build → analysis, with the live state of each.
function StageLine({ stage }) {
  const h = stage.heuristic;
  const a = stage.analysis || {};
  const pv = stage.preview || "none";
  const step = (label, state, tone) => (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-page-divider ${tone}`}>
      <span className="font-semibold">{label}</span><span className="opacity-80">{state}</span>
    </span>
  );
  const arrow = <span className="text-page-muted text-[11px]">→</span>;
  const buildOpen = h && ["queued", "claimed", "running", "waiting"].includes(h.status);
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-page-divider px-3 py-2" data-testid="tasks-stage" data-analysis-state={a.state}>
      {step("Sources", `v${stage.materials_version}`, "text-page-text")}{arrow}
      {step("Preview", pv === "none" ? "—" : pv === "generating" ? "designing…" : pv === "ready" ? "waiting for approval" : pv === "not_needed" ? "settled earlier" : pv,
        pv === "generating" || pv === "ready" || pv === "failed" ? "text-amber-700 dark:text-amber-400" : "text-page-text")}{arrow}
      {step("Build", h ? `#${h.request_id} ${h.status}` : "—", buildOpen ? "text-amber-700 dark:text-amber-400" : h?.status === "done" ? "text-emerald-700 dark:text-emerald-400" : "text-page-text")}{arrow}
      {step("Analysis", a.allowed ? "open" : "gated", a.allowed ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400")}
      <span className="text-[11px] text-page-muted ml-1">
        {a.allowed
          ? "The requirements analysis can run now; it reads what was built and files only the gaps."
          : a.reason}
      </span>
    </div>
  );
}

function BuildRow({ b, projectSlug }) {
  const pv = b.preview || {};
  return (
    <div className="rounded-md border border-page-divider px-2 py-1.5 flex items-center gap-2" data-testid={`build-${b.materials_version}`}>
      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-card-border/40 text-page-muted">{b.kind || "build"}</span>
      <span className="min-w-0 flex-1 truncate text-sm text-page-text" title={b.title || ""}>
        {b.title || (pv.state === "generating" ? "Build preview being designed…" : pv.state === "ready" ? "Build preview waiting for approval" : pv.state === "dismissed" ? "No build for this version" : pv.state === "not_needed" ? "No preview — sources went into the requirements review" : "Build not filed yet")}
      </span>
      <span className="shrink-0 text-[11px] text-page-muted">sources v{b.materials_version}</span>
      {pv.approved_variant ? (
        <span className="shrink-0 text-[11px] text-page-muted" title="The approved preview variant">preview {pv.approved_variant.key} · {pv.approved_variant.title}</span>
      ) : pv.state === "skipped" ? <span className="shrink-0 text-[11px] text-page-muted">no preview</span> : null}
      {b.request_id ? (
        <a href={`/p/${encodeURIComponent(projectSlug)}/b/main/board`}
          className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded no-underline hover:underline ${boardTone(b.status)}`}>
          #{b.request_id} · {boardLabel(b.status)}
        </a>
      ) : <span className="shrink-0 text-[11px] text-page-muted">{pv.state === "dismissed" ? "closed" : "not filed"}</span>}
    </div>
  );
}

function Chip({ tone = "text-page-muted", children }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${tone}`}
      style={{ background: "var(--card-border, rgba(127,127,127,.18))" }}>{children}</span>
  );
}

// A stored screenshot renders as a real thumbnail. The bytes come through the
// authenticated /evidence/:id/image route — an <img src> cannot send the
// bearer token, and a presigned GET URL may name a host only the backend can
// reach — so: fetch → blob → objectURL. An external URL ref stays a plain link.
function Shot({ taskId, e }) {
  const external = /^https?:\/\//i.test(e.ref || "");
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (external) return undefined;
    let dead = false;
    let url = "";
    (async () => {
      try {
        const blob = await fetchData(
          `docs/tasks/${encodeURIComponent(taskId)}/evidence/${e.id}/image`,
          { method: "GET", responseType: "blob" });
        url = URL.createObjectURL(blob);
        if (dead) URL.revokeObjectURL(url); else setSrc(url);
      } catch { if (!dead) setFailed(true); }
    })();
    return () => { dead = true; if (url) URL.revokeObjectURL(url); };
  }, [taskId, e.id, e.ref, external]);
  if (external) {
    return (
      <a href={e.ref} target="_blank" rel="noreferrer" data-testid={`task-evidence-shot-${e.id}`}
        className="inline-flex items-center gap-1 text-[11px] text-nav-active no-underline hover:underline mt-0.5">
        open image <ExternalLink size={10} />
      </a>
    );
  }
  if (failed) return <span className="text-[10px] text-page-muted mt-0.5 font-mono" data-testid={`task-evidence-shot-${e.id}`}>{e.ref} (image unavailable)</span>;
  if (!src) return <span className="inline-flex items-center gap-1 text-[10px] text-page-muted mt-0.5"><Loader2 size={10} className="animate-spin" /> loading image…</span>;
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Open full size" className="inline-block mt-0.5">
      <img src={src} alt={e.title || "screenshot"} data-testid={`task-evidence-shot-${e.id}`}
        className="max-h-40 max-w-full rounded border border-page-divider" />
    </a>
  );
}

function Evidence({ taskId, rows, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState("steps");
  const [body, setBody] = useState("");
  const [ref, setRef] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [shotName, setShotName] = useState("");
  const shotInput = useRef(null);

  const pickShot = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const key = await uploadScreenshot(file);
      setRef(key); setShotName(file.name);
    } catch (ex) { setErr(String(ex?.message || "Could not upload the image.")); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await fetchData(`docs/tasks/${encodeURIComponent(taskId)}/evidence`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, title: title || null, body: body || null, ref: ref || null }),
      });
      setBody(""); setRef(""); setTitle(""); setShotName(""); setAdding(false);
      await onChanged?.();
    } catch (e) { setErr(String(e?.message || "Could not save the evidence.")); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try {
      await fetchData(`docs/tasks/${encodeURIComponent(taskId)}/evidence/${id}`, { method: "DELETE" });
      await onChanged?.();
    } catch { /* the list re-reads either way */ }
  };

  const needsBody = kind === "steps" || kind === "test_notes";
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">Evidence of implementation</div>
      {rows == null ? (
        <div className="text-xs text-page-muted flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> reading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-page-muted">
          Nothing recorded. Evidence is what turns "it was closed" into "here is how you check it".
        </div>
      ) : rows.map((e) => (
        <div key={e.id} className="text-[11px] text-page-muted group" data-testid={`task-evidence-${e.id}`}>
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-page-text">{(EV_KINDS.find((k) => k[0] === e.kind) || [, e.kind])[1]}</span>
            {e.title ? <span>— {e.title}</span> : null}
            {e.ref && e.kind !== "screenshot" ? <span className="font-mono">{e.ref}</span> : null}
            <button type="button" onClick={() => del(e.id)} title="Remove"
              className="ml-auto opacity-0 group-hover:opacity-100 text-page-muted hover:text-red-700 bg-transparent border-0 cursor-pointer p-0 text-[10px]">
              remove
            </button>
          </div>
          {e.body ? <pre className="whitespace-pre-wrap mt-0.5 rounded px-2 py-1" style={{ background: "var(--table-stripe)" }}>{e.body}</pre> : null}
          {e.kind === "screenshot" && e.ref ? <Shot taskId={taskId} e={e} /> : null}
        </div>
      ))}
      {adding ? (
        <div className="space-y-1 rounded-md border border-page-divider p-2" data-testid={`task-evidence-form-${taskId}`}>
          <div className="flex items-center gap-1.5">
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid={`task-evidence-kind-${taskId}`}
              className="text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
              {EV_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="title (optional)"
              className="flex-1 text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text" />
          </div>
          {kind === "screenshot" ? (
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => shotInput.current?.click()} disabled={busy}
                data-testid={`task-evidence-attach-${taskId}`}
                className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 disabled:opacity-50">
                {busy ? <Loader2 size={10} className="animate-spin" /> : <Paperclip size={10} />}
                {shotName || "attach an image"}
              </button>
              <input ref={shotInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden" onChange={pickShot} data-testid={`task-evidence-file-${taskId}`} />
              <span className="shrink-0 text-[10px] text-page-muted">or</span>
              <input value={ref} onChange={(e) => { setRef(e.target.value); setShotName(""); }}
                data-testid={`task-evidence-ref-${taskId}`} placeholder="image URL or key"
                className="min-w-0 flex-1 text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text font-mono" />
            </div>
          ) : !needsBody ? (
            <input value={ref} onChange={(e) => setRef(e.target.value)} data-testid={`task-evidence-ref-${taskId}`}
              placeholder={kind === "commit" ? "commit hash" : "URL"}
              className="w-full text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text font-mono" />
          ) : (
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid={`task-evidence-body-${taskId}`}
              placeholder={kind === "steps" ? "1. Open …\n2. Click …\n3. You should see …" : "What was run, and what it showed."}
              className="w-full text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text font-mono" />
          )}
          {err ? <div className="text-[11px] text-red-600">{err}</div> : null}
          <div className="flex items-center gap-2">
            <button type="button" onClick={save} disabled={busy} data-testid={`task-evidence-save-${taskId}`}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-400 disabled:opacity-50">
              {busy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
            </button>
            <button type="button" onClick={() => setAdding(false)} className="text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer">Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} data-testid={`task-evidence-add-${taskId}`}
          className="inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0">
          <Plus size={10} /> add evidence
        </button>
      )}
    </div>
  );
}
